import { Button, Grid, TextField, Typography } from "@mui/material";
import Config from "../../Utils/Config";
import { Controller, useForm } from "react-hook-form";
import { OblioSettingsValidationSchema } from "../../Utils/Models";
import { yupResolver } from "@hookform/resolvers/yup";
import { Vocabulary } from "../../Utils/Vocabulary";
import { Save } from "@mui/icons-material";
import { endpoints } from "../../Utils/UrlEnum";
import { updateData } from "../../Services/updateData";
import { AxiosResponse } from "axios";
import { WebSocketContext } from "../../Context/WebSocketContext";
import { useContext, useEffect, useState } from "react";
import styles from "../../Styles/settings.module.css";
import { getData } from "../../Services/getData";
import { GreenSwitch } from "../../Utils/Utils";

export default function OblioIntegration() {
  const websocketContext = useContext(WebSocketContext);
  const [isFgoActive, setIsFgoActive] = useState(false);

  const { control, reset, handleSubmit, watch } = useForm({
    resolver: yupResolver(OblioSettingsValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    shouldFocusError: true,
    criteriaMode: "firstError",
  });

  const isActive = watch("value.isActive");

  useEffect(() => {
    getData(endpoints.setting.oblioSettings).then((res: AxiosResponse) => {
      if (res?.data) {
        reset(res.data);
      }
    });
    // Check FGO status
    getData(endpoints.setting.fgoSettings).then((res: AxiosResponse) => {
      if (res?.data?.value?.isActive) {
        setIsFgoActive(true);
      }
    });
  }, [reset]);

  useEffect(() => {
    // Listen for FGO activation to disable Oblio
    const handleFgoActivation = () => {
      getData(endpoints.setting.fgoSettings).then((res: AxiosResponse) => {
        if (res?.data?.value?.isActive) {
          setIsFgoActive(true);
        } else {
          setIsFgoActive(false);
        }
      });
      getData(endpoints.setting.oblioSettings).then((res: AxiosResponse) => {
        if (res?.data) {
          reset(res.data);
        }
      });
    };

    if (websocketContext.shouldUpdate) {
      handleFgoActivation();
    }
  }, [websocketContext.shouldUpdate, reset]);

  const onSubmit = (data: any) => {
    const url = `${endpoints.setting.oblio}${data?._id}`;
    updateData(url, data).then((res: AxiosResponse) => {
      if (res?.data) {
        websocketContext.setShouldUpdate(!websocketContext.shouldUpdate);
        reset(res.data);
      }
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.settingsForm}>
      {/* Active Toggle */}
      <Grid container spacing={Config.standardGridSpacing} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Typography
            component="div"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            {Vocabulary.oblioIsActive}
            <Controller
              name="value.isActive"
              control={control}
              defaultValue={false}
              render={({ field: { value, onChange } }) => (
                <GreenSwitch
                  checked={value}
                  onChange={onChange}
                  disabled={isFgoActive}
                />
              )}
            />
            {isFgoActive && (
              <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                {Vocabulary.disableFgoToActivateOblio}
              </Typography>
            )}
          </Typography>
        </Grid>
      </Grid>

      {/* Input fields */}
      {isActive && !isFgoActive && (
        <Grid container spacing={Config.standardGridSpacing} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Controller
              name="value.url"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={Vocabulary.oblioApiUrl}
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  placeholder="https://example.com"
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="value.clientId"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={Vocabulary.oblioClientId}
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="value.clientSecret"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={Vocabulary.oblioClientSecret}
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  type="password"
                />
              )}
            />
          </Grid>
        </Grid>
      )}

      {/* Save Button */}
      <Grid container spacing={Config.standardGridSpacing}>
        <Grid item xs={12} md={3}>
          <Button
            type="submit"
            fullWidth
            startIcon={<Save />}
            variant="contained"
          >
            {Vocabulary.save}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
